import type { TFindAllStructurePartnersResponse } from "@api";
import {
  Autocomplete,
  type AutocompleteProps,
  CircularProgress,
  Divider,
  Link,
  Paper,
  Stack,
  TextField,
  type TextFieldProps,
  Typography,
  createFilterOptions,
} from "@mui/material";
import { type ElementType, useEffect } from "react";
import { useAutocompleteStructurePartners } from "./logics";

export type TStructurePartnerOption = Omit<TFindAllStructurePartnersResponse[number], "_id"> & { _id?: string };

export type TAutocompleteStructurePartnersProps<
  Multiple extends boolean | undefined = undefined,
  ChipComponent extends React.ElementType = "div",
> = Omit<
  AutocompleteProps<TStructurePartnerOption, Multiple, false, false, ChipComponent>,
  "getOptionLabel" | "renderOption" | "renderInput" | "options"
> & {
  TextFieldProps: Omit<TextFieldProps, "value" | "onChange">;
};

const filter = createFilterOptions<TStructurePartnerOption>();

export const AutocompleteStructurePartners = <
  Multiple extends boolean | undefined = undefined,
  ChipComponent extends ElementType = "div",
>({
  TextFieldProps,
  ...props
}: TAutocompleteStructurePartnersProps<Multiple, ChipComponent>) => {
  const { options, onChange, loading, inputValue, setInputValue } = useAutocompleteStructurePartners();

  useEffect(() => {
    const input = Array.isArray(props.value) ? props.value[0]?.name : props.value?.name;
    onChange(undefined, input ?? "");
  }, []);

  return (
    <Autocomplete
      {...props}
      onInputChange={onChange}
      options={options}
      isOptionEqualToValue={(option, value) => !!option && !!value && option._id === value._id}
      getOptionLabel={(option) => option.name}
      filterOptions={(currentOptions, params) => {
        const filtered = filter(currentOptions, params);
        if (params.inputValue !== "" && !currentOptions.some((option) => option.name === params.inputValue)) {
          filtered.push({ name: params.inputValue });
        }
        return filtered;
      }}
      renderOption={(props, option) =>
        option._id ? (
          <li {...props}>
            <Stack>
              <Typography variant="body1SemiBold">{option.name}</Typography>
              <Typography variant="body2" color={option.place ? "textPrimary" : "textSecondary"}>
                {option.place?.placeLabel || "Structure non physique"}
              </Typography>
            </Stack>
          </li>
        ) : (
          <Stack
            component="li"
            sx={{
              mt: 1,
              gap: 1,
            }}
          >
            <Divider sx={{ mx: 2 }} />
            <Typography {...props} variant="body1SemiBold" color="primary" sx={{ alignSelf: "stretch" }}>
              + Renseigner un nouveau partenaire
            </Typography>
          </Stack>
        )
      }
      renderInput={(params) => (
        <TextField {...params} {...TextFieldProps} value={inputValue} onChange={(e) => setInputValue(e.target.value)} />
      )}
      slots={{
        paper: (props) => (
          <Paper {...props}>
            {props.children}
            <Divider />
            <Typography variant="body1" sx={{ fontStyle: "italic", px: 2, mt: 1, pb: 2 }}>
              Si vous ne trouvez pas la structure que vous cherchez, vous pouvez la créer depuis{" "}
              <Link
                sx={{ fontWeight: "bold" }}
                onMouseDown={(e) => e.preventDefault()}
                href="/partners/structure/dashboard"
                target="_blank"
              >
                le tableau de bord des partenaires
              </Link>
              .
            </Typography>
          </Paper>
        ),
      }}
      noOptionsText={`${inputValue.length < 2 ? "Pas de résultat. Saisissez au moins 2 caractères pour avoir des propositions de structures partenaires." : "Pas de résultat."}`}
      {...(props.multiple && { open: options.length > 0 })}
      loading={loading}
      loadingText={<CircularProgress color="primary" size={16} />}
    />
  );
};
